import { useRef, useState } from 'react';

import { DataGrid, Page } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { useBuildingInspectionContext } from '@/pages/Tools/BuildingInspection/BuildingInspectionContext';
import { useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

import { buildingRecordDataGridCols } from '../dummy_data/dummy_data';

interface MenuState {
  menuEl: HTMLElement | null;
  vehicle?: any;
}

export const BuildingRecordGrid = () => {
  const { user } = useUserStore();
  const { navigateWithSearch } = useSearchPersist();
  const { buildingRecords } = useBuildingInspectionContext();
  const [menuState, setMenuState] = useState<MenuState>({
    menuEl: null,
  });
  const menuRef = useRef<HTMLDivElement>(null);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `vehicles-list-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return buildingRecordDataGridCols;
      }

      return mergeColumnSettings(buildingRecordDataGridCols, savedSettings);
    },
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any, // Adjust type as needed
  ) => {
    event.stopPropagation();
    setMenuState({
      menuEl: event.currentTarget,
      vehicle: row,
    });
  };

  const columns = useDataGridColumns({
    type: 'vehicleListCols',
    columnSettings,
    openMenu: handleMenuOpen,
  }).map((column) => {
    return { ...column, sortable: true };
  });

  const handleMenuClose = () => {
    setMenuState({ menuEl: null });
  };

  const handleView = () => {
    navigateWithSearch(`/tools/vehicle-maintenance/${menuState.vehicle?._id}`);
    handleMenuClose();
  };

  return (
    <Page title="Building Inspection">
      <div className="h-[calc(100vh-150px)] overflow-auto pt-3">
        <DataGrid
          key="building-inspection"
          name="building-inspection-list"
          autoPageSize={false}
          persistSearch
          clientMode
          clientRows={buildingRecords}
          columnSettings={columnSettings}
          setColumnSettings={setColumnSettings}
          onRowClick={(params) =>
            navigateWithSearch(`/tools/building-inspection/${params.row._id}`)
          }
          columns={columns}
          disableMultipleColumnsSorting={true}
        />

        {menuState.menuEl && (
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              left: menuState.menuEl?.getBoundingClientRect().left,
              top: menuState.menuEl?.getBoundingClientRect().bottom,
              zIndex: 1000,
            }}
          >
            <div className="bg-white border border-gray-200 rounded-md shadow-lg w-32">
              <button
                onClick={handleView}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                View
              </button>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
};
